import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Platform, View, Modal, TextInput, ScrollView, Pressable } from 'react-native';
import apis from '@api/api';
import { useSelector, useDispatch } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import BottomSheet, {
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet';
import AnimatedLottieView from '@utils/lottieAnimation';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import { NavigationHelpers, useFocusEffect, useRoute } from '@react-navigation/native';
import { CLEAR_ACTION_HANDLER_HISTORY, CLEAR_UPLOADER_HISTORY, HIDE_TOOLBAR, SET_ACTIONS, SHOW_TOOLBAR, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { MaterialIcons } from '@expo/vector-icons';
import { DefaultRootState } from '@reduxLocal/persistState';
import ActionHierarchy, { CombinedHandle } from './Components/ActionHierarchy';
import FlowHistory from './Components/FlowHistory';
import { MermaidHierarchyAdapter } from '@components/Hierarchy/adapters';
import getGlobalUser from '@hooks/getGlobalUser';
import { NativeStackNavigationHelpers, NativeStackNavigationProp } from '@react-navigation/native-stack/lib/typescript/src/types';
import { AddUserModalHandle } from '../../UsersScreen/Components/AddUserModal';
import { UserOptionsModalHandle } from '../../UsersScreen/Components/UserOptionsModal';
import { YStack, XStack, H4, H6, SizableText, Card, Button, Separator } from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import { CountUp } from 'use-count-up';
import { noMonitor } from '@api/api';
import { Text } from '@constants/Themed';
import toTitleCase from '@hooks/toTitleCase';
import parseByDelimiter from '@utils/parseByDelimiter';

type ActionHandlerTypes = {
  navigation: NativeStackNavigationProp<Record<string, object | { dataId?: string, dataTitle?: string }>, any>,
}

// AI Flow Suggestion Modal Component
const AIFlowSuggestionModal = ({ visible, onClose, dataId, dataTitle, currentActions }) => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetSuggestions = useCallback(async () => {
    if (!description || description.trim().length < 10) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please enter at least 10 characters', type: 'warning' }
      });
      return;
    }

    setLoading(true);
    try {
      const response = await noMonitor.post('/ai/flow/suggest-automation', {
        description,
        dataSchemaId: dataId
      });

      if (response.data.success && response.data.flows) {
        setSuggestions(response.data.flows);
      }
    } catch (error) {
      console.error('Failed to get AI flow suggestions:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to get AI suggestions', type: 'error' }
      });
    }
    setLoading(false);
  }, [description, dataId, dispatch]);

  const handleApplyFlow = useCallback((flow) => {
    // Convert AI flow stages to action format
    const actions = flow.stages.map(stage => ({
      key: stage.key,
      icon: stage.icon,
      title: stage.title,
      actionKey: stage.actionKey,
      type: stage.type,
      config: stage.config || {}
    }));

    // Dispatch actions to Redux
    dispatch({ type: SET_ACTIONS, payload: actions });

    dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: `Applied: ${flow.name}`, type: 'success' }
    });

    onClose();
    setSuggestions([]);
    setDescription('');
  }, [dispatch, onClose]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={{
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }} onPress={onClose}>
        <Pressable style={{
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 20,
          padding: 24,
          width: Platform.OS === 'web' ? 600 : '90%',
          maxHeight: '80%',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.3,
          shadowRadius: 20
        }} onPress={(e) => e.stopPropagation()}>

          <H6 style={{ marginBottom: 16, color: 'rgb(90, 192, 234)' }}>
            🤖 AI Flow Assistant
          </H6>

          <Text style={{ marginBottom: 12, fontSize: 14 }}>
            Describe what you want to automate for: {dataTitle}
          </Text>

          <TextInput
            placeholder="e.g., Send welcome email when customer is added"
            value={description}
            onChangeText={setDescription}
            multiline
            style={{
              backgroundColor: theme ? '#f3f4f6' : '#374151',
              borderRadius: 12,
              padding: 12,
              marginBottom: 16,
              minHeight: 80,
              textAlignVertical: 'top',
              color: theme ? '#000' : '#fff'
            }}
          />

          <Pressable
            onPress={handleGetSuggestions}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#ccc' : 'rgb(90, 192, 234)',
              borderRadius: 12,
              padding: 14,
              alignItems: 'center',
              marginBottom: 16
            }}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>
              {loading ? 'Getting Suggestions...' : 'Get AI Suggestions'}
            </Text>
          </Pressable>

          <ScrollView style={{ maxHeight: 400 }}>
            {suggestions.length > 0 && (
              <View style={{ gap: 12 }}>
                <Text style={{ fontWeight: '600', marginBottom: 8 }}>
                  Suggested Flows:
                </Text>
                {suggestions.map((flow, index) => (
                  <Pressable
                    key={index}
                    onPress={() => handleApplyFlow(flow)}
                    style={{
                      backgroundColor: 'rgba(90, 192, 234, 0.1)',
                      borderRadius: 12,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: 'rgba(90, 192, 234, 0.3)'
                    }}
                  >
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontWeight: '600', flex: 1 }}>
                        {flow.name}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#666' }}>
                        {((flow.confidence || 0.5) * 100).toFixed(0)}% match
                      </Text>
                    </View>

                    <Text style={{ fontSize: 13, marginBottom: 8, color: '#666' }}>
                      {flow.description}
                    </Text>

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {flow.actions && flow.actions.map((action, i) => (
                        <View key={i} style={{
                          backgroundColor: 'rgb(90, 192, 234)',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <Text style={{ color: 'white', fontSize: 11 }}>
                            {action}
                          </Text>
                        </View>
                      ))}
                    </View>

                    {flow.requires_setup && flow.requires_setup.length > 0 && (
                      <Text style={{ fontSize: 11, color: 'orange', marginTop: 6 }}>
                        ⚠️ Requires: {flow.requires_setup.join(', ')}
                      </Text>
                    )}

                    {flow.fallback && (
                      <Text style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                        (Basic suggestion - AI service unavailable)
                      </Text>
                    )}
                  </Pressable>
                ))}
              </View>
            )}
          </ScrollView>

          <Pressable
            onPress={onClose}
            style={{
              marginTop: 16,
              padding: 12,
              alignItems: 'center',
              borderRadius: 12,
              backgroundColor: theme ? '#f3f4f6' : '#374151'
            }}
          >
            <Text style={{ color: theme ? '#000' : '#fff' }}>Close</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const ActionHandlerScreen = ({ navigation }: ActionHandlerTypes) => {
  // Use useRoute hook to get params reactively (updates when navigating with different params)
  const route = useRoute();
  const params = route.params as { dataId?: string, dataTitle?: string, id?: string } | undefined;
  const id = params?.dataId || params?.id;
  const dataTitle = params?.dataTitle;
  const toolbarDisplayed = useSelector((state: DefaultRootState) => state.modals.toolbar.toolbarDisplayed);
  const zoomableViewRef = useRef<CombinedHandle>(null);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [listVisible, setListVisible] = useState(false);
  const [userList, setUserList] = useState([{}]);
  const [loaded, setLoaded] = useState(false);
  const addUserRef = useRef<AddUserModalHandle>(null);
  const [helpState, setHelpState] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [flowHistoryVisible, setFlowHistoryVisible] = useState(false);
  const [agenticFlows, setAgenticFlows] = useState<any[]>([]);
  const [showMermaidPreview, setShowMermaidPreview] = useState(false);
  const userId = getGlobalUser();
  const helpText = ['Create powerful custom actions for your data', 'You can add an action on data submission by using the arrow at the bottom', 'You can also link fields with other data that you have stored in SmartSaaS to create links between the data']
  const links = useSelector((state: DefaultRootState) => state.actionMaker.present.actions);


  const dispatchUserToolbar = () => {
    dispatch({
      type: SHOW_TOOLBAR,
      payload: {
        draggable: false,
        historyTracked: false,
        component: 'user',
        refId: 'userRefId',
        items: [
          { id: "history", toolName: "History", icon: "history", stateName: "actionMaker", description: "View the history of the user", action: "history" },
          { id: 'zoomIn', toolName: 'Zoom In', icon: 'plus-square', description: 'Zoom in on the map', action: "zoomIn" },
          { id: 'zoomOut', toolName: 'Zoom Out', icon: 'minus-square', description: 'Zoom out on the map', action: "zoomOut" },
          { id: 'centerView', toolName: 'Center', icon: 'plus-circle', description: 'Center the view on the map', action: "centerView" },
          { id: 'help', toolName: 'Help', icon: 'question-circle', description: 'Open the help menu', action: "help" },
        ]
      }
    });
    setLoaded(true)
  };


  const _handleReset = useCallback(() => {
    setLoaded(false);
    setTimeout(() => {
      setLoaded(true);
    }, 1000);
  }, [loaded, userList])

  function _handleSnap(value) {
    console.log('trying to snap', value);
    if (value && value !== "" && value !== null && value !== undefined) {
      setSelectedUser(value);
      // userOptionsRef.current?.openModal(value);
      // const userProfile = userList.find((user) => user.user._id === value);
      // console.log(userProfile);
      // try {
      //   if (userOptionsRef.current) {
      //     userOptionsRef.current.openModal(userProfile);
      //   }
      // } catch (e) {
      //   console.log(e);
      // }
    }
  }

  // Get current Redux actions to prevent overriding
  const currentReduxActions = useSelector((state: DefaultRootState) => state.actionMaker.present.actions || []);

  // Store last saved state to detect changes - keyed by dataId
  const lastSavedActionsRef = useRef<Record<string, string>>({});
  const previousIdRef = useRef<string | undefined>(undefined);

  // Clear Redux actions when dataId changes
  useEffect(() => {
    if (id && id !== previousIdRef.current) {
      console.log('🔄 ActionHandlerScreen: dataId changed, clearing Redux actions', {
        previousId: previousIdRef.current,
        newId: id
      });
      dispatch({ type: SET_ACTIONS, payload: [] });
      previousIdRef.current = id;
    }
  }, [id, dispatch]);

  // Load agentic flows for this data schema
  useEffect(() => {
    const loadAgenticFlows = async () => {
      if (!id) return;
      try {
        const response = await apis.getAgenticFlows(id);
        if (response?.success && response?.flows) {
          setAgenticFlows(response.flows);
        }
      } catch (error) {
        console.error('Error loading agentic flows:', error);
      }
    };
    loadAgenticFlows();
  }, [id]);

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['schemaActions', id], // Include id in query key so it refetches when id changes
    queryFn: async () => {
      if (!id) {
        console.warn('⚠️ ActionHandlerScreen: No id provided, skipping query');
        return null;
      }

      const data = await apis.getDataSchema(userId, id)
        .then((res) => {
          return res.data.data;
        })

      // Always set actions when dataId changes (query key change triggers this)
      if (data?.actions?.length) {
        console.log('🎯 ActionHandlerScreen: Loading actions from backend for dataId:', id, data.actions);
        // Normalize actions to ensure actionType and type fields are consistent
        const normalizedActions = data.actions.map((action: any) => {
          // Ensure both actionType and type fields are set correctly
          const actionType = action.actionType || action.type || 'create';
          return {
            ...action,
            actionType: actionType.toLowerCase(),
            type: actionType.toLowerCase(), // Ensure type field is set for DraggableActionCard compatibility
          };
        });
        dispatch({ type: SET_ACTIONS, payload: normalizedActions })
        // Initialize last saved state with loaded actions
        lastSavedActionsRef.current[id] = JSON.stringify(normalizedActions);
      } else {
        console.log('🎯 ActionHandlerScreen: No backend actions for dataId:', id);
        dispatch({ type: SET_ACTIONS, payload: [] })
        // Initialize last saved state with empty array
        lastSavedActionsRef.current[id] = JSON.stringify([]);
      }

      return data;
    },
    enabled: !!id, // Only run query if id is available
    staleTime: 30000, // 30 seconds - allow refetching when navigating between different data items
  });

  // Function to check if actions have changed
  const hasActionsChanged = useCallback(() => {
    if (!id) return false;
    
    const currentActionsString = JSON.stringify(currentReduxActions);
    const lastSavedString = lastSavedActionsRef.current[id];

    // If we haven't saved yet for this id, consider it changed if there are actions
    if (!lastSavedString) {
      return currentReduxActions.length > 0;
    }

    // Compare current state with last saved state for this id
    return currentActionsString !== lastSavedString;
  }, [currentReduxActions, id]);

  // Function to save actions to backend
  const saveActionsToBackend = useCallback(async () => {
    try {
      // Only save if there are actual changes
      if (!hasActionsChanged()) {
        console.log('⏭️ Skipping autosave - no changes detected');
        return;
      }

      if (currentReduxActions.length > 0 && id) {
        const response = await apis.updateDataActions(id, currentReduxActions);
        if (response && response.status === 200) {
          console.log('✅ Actions saved successfully to backend for dataId:', id);
          // Update last saved state after successful save (id-specific)
          lastSavedActionsRef.current[id] = JSON.stringify(currentReduxActions);
        } else {
          console.error('❌ Failed to save actions to backend:', response);
        }
      }
    } catch (error) {
      console.error('❌ Error saving actions to backend:', error);
    }
  }, [id, currentReduxActions, hasActionsChanged]);

  // Auto-save actions every 30 seconds if there are changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentReduxActions.length > 0) {
        saveActionsToBackend();
      }
    }, 30000); // Save every 30 seconds

    return () => clearInterval(interval);
  }, [saveActionsToBackend, currentReduxActions.length]);

  useFocusEffect(
    useCallback(() => {
      const beforeRemoveListener = () => {
        // Save changes before leaving (only if there are changes)
        saveActionsToBackend();
        dispatch({ type: CLEAR_ACTION_HANDLER_HISTORY });
      };
      if (!loaded) {
        dispatchUserToolbar();
      }
      // Clean up the listener when the component loses focus
      return () => { dispatch({ type: HIDE_TOOLBAR }); dispatch({ type: CLEAR_ACTION_HANDLER_HISTORY }); navigation.removeListener('beforeRemove', beforeRemoveListener) };
    }, [navigation, saveActionsToBackend, loaded, dispatchUserToolbar])
  );

  const _openAddUserModal = useCallback(() => {
    addUserRef.current?.openModal();
  }, [addUserRef]);


  return (
    <View style={{ flex: 1 }}>
      <AIFlowSuggestionModal
        visible={aiModalVisible}
        onClose={() => setAiModalVisible(false)}
        dataId={id}
        dataTitle={dataTitle || data?.title}
        currentActions={links}
      />
      <DefaultScreen fullWidth style={{ paddingTop: 0 }} containerStyle={{ paddingTop: 0 }} noMargin scrollDisabled>
        <YStack flex={1} backgroundColor="transparent">
          {/* Header Section */}
          <Animatable.View
            animation="fadeInDown"
            duration={600}
          >
            {Platform.OS === 'web' && (
              <XStack
                shadowRadius={20}
                shadowOpacity={0.2}
                backgroundColor={theme ? "#ececec" : "#333333"}
                borderBottomLeftRadius={20}
                borderBottomRightRadius={20}
                paddingHorizontal="$4"
                paddingVertical="$3"
                gap="$3"
                alignItems="center"
              >
                <Button
                  size="$3"
                  backgroundColor="#ef4444"
                  onPress={() => {
                    dispatch({ type: CLEAR_ACTION_HANDLER_HISTORY });
                    navigation.goBack();
                  }}
                  borderRadius={12}
                  icon={<MaterialIcons name="arrow-back" size={18} color="white" />}
                />

                <SizableText
                  flex={1}
                  fontSize={10}
                  fontWeight="bold"
                  numberOfLines={1}
                >
                  {parseByDelimiter(toTitleCase(dataTitle || data?.title || 'Loading...'))}
                </SizableText>

                <XStack gap="$2" alignItems="center">
                  <LinearGradient
                    colors={['#1E90FF', '#58BFEA']}
                    style={{
                      minWidth: 50,
                      paddingVertical: 14,
                      maxWidth: 50,
                      height: 50,
                      borderRadius: 10,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <SizableText color="white" textAlign="center">
                      <CountUp isCounting end={links?.length || 0} duration={2} />
                    </SizableText>
                  </LinearGradient>
                  <SizableText fontSize={16}>Current Actions</SizableText>
                </XStack>

                <XStack gap="$2" alignItems="center">
                  <LinearGradient
                    colors={['#1E90FF', '#58BFEA']}
                    style={{
                      minWidth: 50,
                      paddingVertical: 14,
                      maxWidth: 50,
                      height: 50,
                      borderRadius: 10,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Pressable onPress={() => setAiModalVisible(true)} style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <SizableText fontSize={20}>🤖</SizableText>
                    </Pressable>
                  </LinearGradient>
                  <SizableText fontSize={16} numberOfLines={1}>AI Suggestions</SizableText>
                </XStack>

                <XStack gap="$2" alignItems="center">
                  <LinearGradient
                    colors={['#10b981', '#059669']}
                    style={{
                      minWidth: 50,
                      paddingVertical: 14,
                      maxWidth: 50,
                      height: 50,
                      borderRadius: 10,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Pressable onPress={() => setFlowHistoryVisible(true)} style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <MaterialIcons name="history" size={24} color="white" />
                    </Pressable>
                  </LinearGradient>
                  <SizableText fontSize={16} numberOfLines={1}>Flow History</SizableText>
                </XStack>

                <Button
                  size="$3"
                  backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                  onPress={() => saveActionsToBackend()}
                  borderRadius={12}
                  icon={<MaterialIcons name="save" size={18} color="white" />}
                />
              </XStack>
            )}

            {/* Action Buttons */}
            <XStack
              paddingHorizontal="$4"
              paddingVertical="$3"
              gap="$2"
              flexWrap="wrap"
              justifyContent="center"
            >
              <Button
                size="$3"
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                onPress={() => {
                  if (zoomableViewRef.current?.centerView) {
                    zoomableViewRef.current.centerView();
                  }
                }}
                borderRadius={12}
              >
                <XStack alignItems="center" gap="$1">
                  <MaterialIcons name="center-focus-strong" size={16} color="white" />
                  <SizableText color="white" size="$2" fontWeight="600">Center</SizableText>
                </XStack>
              </Button>
              <Button
                size="$3"
                backgroundColor={theme ? "#f59e0b" : "#d97706"}
                onPress={() => {
                  if (zoomableViewRef.current?.zoomBy) {
                    zoomableViewRef.current.zoomBy(-0.2);
                  }
                }}
                borderRadius={12}
              >
                <XStack alignItems="center" gap="$1">
                  <MaterialIcons name="zoom-out" size={16} color="white" />
                  <SizableText color="white" size="$2" fontWeight="600">Zoom Out</SizableText>
                </XStack>
              </Button>
              <Button
                size="$3"
                backgroundColor={theme ? "#10b981" : "#059669"}
                onPress={() => {
                  if (zoomableViewRef.current?.zoomBy) {
                    zoomableViewRef.current.zoomBy(0.2);
                  }
                }}
                borderRadius={12}
              >
                <XStack alignItems="center" gap="$1">
                  <MaterialIcons name="zoom-in" size={16} color="white" />
                  <SizableText color="white" size="$2" fontWeight="600">Zoom In</SizableText>
                </XStack>
              </Button>
              {__DEV__ && (
                <Button
                  size="$3"
                  backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                  onPress={() => setShowMermaidPreview(true)}
                  borderRadius={12}
                >
                  <XStack alignItems="center" gap="$1">
                    <MaterialIcons name="integration-instructions" size={16} color="white" />
                    <SizableText color="white" size="$2" fontWeight="600">Test Mermaid</SizableText>
                  </XStack>
                </Button>
              )}
            </XStack>

            {/* Separator */}
            <Separator
              borderColor={theme ? "#e2e8f0" : "#334155"}
              marginHorizontal="$4"
              marginBottom="$3"
            />
          </Animatable.View>

          {/* Action Hierarchy View */}
          <View style={{ flex: 1 }}>
            {__DEV__ && showMermaidPreview ? (
              <View style={{ flex: 1, position: 'relative' }}>
                {/* Close Mermaid Preview Button */}
                <Pressable
                  onPress={() => setShowMermaidPreview(false)}
                  style={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 1000,
                    backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)',
                    padding: 12,
                    borderRadius: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 4,
                    elevation: 4
                  }}
                >
                  <MaterialIcons name="close" size={20} color={theme ? '#ef4444' : '#f87171'} />
                  <SizableText color={theme ? '#1e293b' : '#f1f5f9'} fontWeight="600">
                    Close Mermaid Preview
                  </SizableText>
                </Pressable>

                {/* Mermaid Hierarchy Adapter Preview */}
                <MermaidHierarchyAdapter
                  mermaidSpec={`
graph TD
    A[Start Action] --> B{Decision Point}
    B -->|Yes| C[Email Notification]
    B -->|No| D[Skip Email]
    C --> E[Update Database]
    D --> E
    E --> F[Log Activity]
    F --> G[End]
    
    style A fill:#3b82f6,stroke:#1e40af,color:#fff
    style B fill:#f59e0b,stroke:#d97706,color:#fff
    style G fill:#10b981,stroke:#059669,color:#fff
                  `}
                  viewportSize={Platform.select({
                    ios: { width: 2500, height: 2500 },
                    android: { width: 4000, height: 4000 },
                    default: { width: 4000, height: 4000 }
                  })}
                  gridSize={50}
                  initialZoom={0.6}
                  minZoom={0.2}
                  maxZoom={Platform.select({
                    ios: 2.5,
                    default: 3
                  })}
                  positioningStrategy="circular"
                  centerSpacing={400}
                  showGrid={true}
                  showDebugInfo={true}
                />
              </View>
            ) : (
              <ActionHierarchy
                isLoading={isLoading}
                ref={zoomableViewRef}
                schemaData={data}
                openAddActionModal={_openAddUserModal}
                handleSnap={_handleSnap}
                dataId={id}
              />
            )}
          </View>

          {/* Help Dialog */}
          {helpState && (
            <RobotHelper
              {...({
                style: {
                  marginHorizontal: '20%',
                  bottom: '50%',
                  maxHeight: 200,
                  minWidth: Platform.OS === 'web' ? 400 : 200
                },
                helpText: helpText,
                setState: setHelpState
              } as any)}
            />
          )}
        </YStack>
      </DefaultScreen>

      {/* Flow History Modal */}
      {flowHistoryVisible && (
        <FlowHistory
          dataId={id}
          visible={flowHistoryVisible}
          onClose={() => setFlowHistoryVisible(false)}
        />
      )}
    </View>
  );
}

export default ActionHandlerScreen;
